import React from 'react';
import { TrendingUp, TrendingDown, Clock, Shield, BarChart3, Trophy, Activity, Briefcase, Newspaper, AlertTriangle, Info, Sparkles, Pickaxe, Layers } from 'lucide-react';

function AboutPage() {
  return (
    <div className="page-container pb-24">
      {/* Header */}
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold font-display flex items-center gap-2">
            <Info className="w-6 h-6 text-blue-400" />
            About TiwiStock
          </h1>
          <p className="text-sm text-muted-foreground">How every feature works — explained in detail</p>
        </div>
      </header>

      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Hero */}
        <div className="card bg-gradient-to-br from-green-900/20 to-background border-green-500/20" data-testid="about-hero">
          <h2 className="text-xl font-bold font-display mb-2">ASX Stock Recommender</h2>
          <p className="text-muted-foreground text-sm">
            TiwiStock brings together analyst consensus, market activity, recovery potential, and fundamental analysis to help Australian investors make informed decisions. All data is sourced from Yahoo Finance and broker research via MarketIndex.
          </p>
        </div>

        {/* ============ 1. TOP PICKS ============ */}
        <div className="card" data-testid="about-top-picks">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-6 h-6 text-green-500" />
            <h2 className="text-lg font-semibold">Top Picks</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Curated stock recommendations based on <strong className="text-foreground">broker analyst consensus</strong> from MarketIndex. Only stocks rated "Strong Buy" by multiple analysts are included.
          </p>

          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">How the Score is Calculated</h3>
            <div className="p-3 rounded bg-muted/30 font-mono text-xs text-muted-foreground">
              Score = Buy Count x Growth Potential %
            </div>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">Buy Count</strong> — Number of analysts who rate the stock as "Buy"</p>
              <p><strong className="text-foreground">Growth Potential %</strong> — ((Analyst Target Price - Current Price) / Current Price) x 100</p>
              <p><strong className="text-foreground">Example:</strong> If 9 analysts rate a stock "Buy" and their average target is 85% above the current price, the score = 9 x 85.3 = <strong className="text-green-400">767.7</strong></p>
            </div>

            <h3 className="font-semibold mt-4">What the Stats Mean</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">High Score</strong> — Count of stocks with a score above 300 (strong analyst conviction + high growth target)</p>
              <p><strong className="text-foreground">Most Active</strong> — Count of Top Picks that also appear in the Favourite Stocks list (cross-referenced automatically). Shown with a blue "Active" badge.</p>
              <p><strong className="text-foreground">Rating</strong> — Number of analysts with a "Buy" recommendation</p>
              <p><strong className="text-foreground">Growth %</strong> — Percentage upside from current price to analyst target</p>
            </div>

            <h3 className="font-semibold mt-4">Sorting</h3>
            <p className="text-muted-foreground text-xs">Stocks are sorted by score in descending order. The highest combination of analyst buy ratings AND growth potential appears first.</p>

            <h3 className="font-semibold mt-4">Data Source</h3>
            <p className="text-muted-foreground text-xs">Manually uploaded by the admin from <strong className="text-foreground">MarketIndex</strong> broker consensus data. Updated regularly.</p>
          </div>
        </div>

        {/* ============ 2. SMART RANKINGS ============ */}
        <div className="card" data-testid="about-smart-rankings">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-6 h-6 text-yellow-500" />
            <h2 className="text-lg font-semibold">Smart Rankings</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Combines data from <strong className="text-foreground">three different sources</strong> — Top Picks, Favourite Stocks, and High Potential — into a single weighted score out of 100. Stocks appearing in multiple lists receive a bonus.
          </p>

          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">The Three Score Components</h3>
            <div className="space-y-2">
              {[
                { source: 'Analyst Score (from Top Picks)', weight: 'Default 40%', desc: 'Based on the stock\'s broker consensus score (buy count x growth potential). Normalised: the stock with the highest score gets 100, others are proportional.', color: 'bg-purple-500' },
                { source: 'Value Score (from High Potential)', weight: 'Default 40%', desc: 'Based on upside potential — how far the stock has dropped from its 52-week high. The stock with the biggest upside gets 100, others are proportional.', color: 'bg-green-500' },
                { source: 'Activity Score (from Favourite Stocks)', weight: 'Default 20%', desc: 'Based on trading value (price x volume). The most actively traded stock gets 100, others are proportional.', color: 'bg-blue-500' },
              ].map(({ source, weight, desc, color }) => (
                <div key={source} className="p-3 rounded bg-muted/30">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${color}`}></div>
                      <span className="font-medium text-xs">{source}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{weight}</span>
                  </div>
                  <p className="text-muted-foreground text-xs ml-4">{desc}</p>
                </div>
              ))}
            </div>

            <h3 className="font-semibold mt-4">Formula</h3>
            <div className="p-3 rounded bg-muted/30 font-mono text-xs text-muted-foreground">
              Final Score = (Analyst x 0.4) + (Value x 0.4) + (Activity x 0.2) + Overlap Bonus
            </div>

            <h3 className="font-semibold mt-4">Overlap Bonus</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>A stock that appears in <strong className="text-foreground">2 lists</strong> gets <strong className="text-yellow-400">+10 bonus points</strong></p>
              <p>A stock that appears in <strong className="text-foreground">all 3 lists</strong> gets <strong className="text-yellow-400">+20 bonus points</strong></p>
              <p>The final score is capped at 100.</p>
            </div>

            <h3 className="font-semibold mt-4">Adjustable Weights</h3>
            <p className="text-muted-foreground text-xs">Use the sliders at the top of the Smart Rankings page to change how much each source influences the final score. Rankings update instantly as you adjust.</p>
          </div>
        </div>

        {/* ============ 3. FAVOURITE STOCKS ============ */}
        <div className="card" data-testid="about-active-stocks">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-6 h-6 text-blue-500" />
            <h2 className="text-lg font-semibold">Favourite Stocks (Active)</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            A handpicked selection of the <strong className="text-foreground">most actively traded ASX stocks</strong> — companies generating the highest trading volume and market interest.
          </p>

          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">What You See</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">Volume</strong> — Number of shares traded</p>
              <p><strong className="text-foreground">Value Traded</strong> — Total dollar value of trades (Price x Volume). You can toggle between sorting by volume or value.</p>
              <p><strong className="text-foreground">Change %</strong> — Today's price change percentage (green = up, red = down)</p>
            </div>

            <h3 className="font-semibold mt-4">Data Source</h3>
            <p className="text-muted-foreground text-xs">Manually uploaded by the admin. Prices are updated daily at 5:15 PM AEST via Yahoo Finance.</p>
          </div>
        </div>

        {/* ============ 4. HIGH POTENTIAL ============ */}
        <div className="card" data-testid="about-high-potential">
          <div className="flex items-center gap-3 mb-4">
            <TrendingDown className="w-6 h-6 text-yellow-500" />
            <h2 className="text-lg font-semibold">High Potential (52-Week Analysis)</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Stocks that have <strong className="text-foreground">fallen 30% or more from their 52-week high</strong>. These are potential recovery plays with significant upside if they return towards their previous highs.
          </p>

          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">Selection Criteria</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>1. Stock must be priced <strong className="text-foreground">above $1.00</strong> (filters out penny stocks)</p>
              <p>2. Current price must be <strong className="text-foreground">30% or more below</strong> its 52-week high</p>
              <p>3. Scanned from a universe of <strong className="text-foreground">250+ ASX stocks</strong></p>
            </div>

            <h3 className="font-semibold mt-4">Key Metrics</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">Drop from High %</strong> — How far the stock has fallen from its 52-week high. Shown in red on the right side. Stocks with the biggest drops appear first.</p>
              <p><strong className="text-foreground">Upside to High %</strong> — The potential percentage gain if the stock recovers to its 52-week high. Shown in green below the drop.</p>
              <p><strong className="text-foreground">52w Low / 52w High</strong> — The lowest and highest prices the stock traded at in the past 52 weeks.</p>
              <p><strong className="text-foreground">Change %</strong> — Today's price change.</p>
            </div>

            <h3 className="font-semibold mt-4">The Low-to-High Bar</h3>
            <p className="text-muted-foreground text-xs">The progress bar shows where the current price sits within the 52-week range:</p>
            <div className="flex items-center gap-2 text-xs mt-1 mb-1">
              <span className="text-yellow-500 font-medium">Low</span>
              <div className="flex-1 bg-border rounded-full h-3">
                <div className="bg-gradient-to-r from-yellow-500 to-blue-500 h-3 rounded-full" style={{width: '25%'}}></div>
              </div>
              <span className="text-blue-400 font-medium">High</span>
            </div>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>Left edge = 52-week low. Right edge = 52-week high.</p>
              <p>A bar that is barely filled means the stock is near its low — maximum recovery potential.</p>
              <p>A bar that is mostly filled means the stock has already recovered significantly.</p>
            </div>

            <h3 className="font-semibold mt-4">Sorting</h3>
            <p className="text-muted-foreground text-xs">Sorted by <strong className="text-foreground">drop from high</strong> (descending). Stocks with the biggest drops from their peak appear first, as they have the most recovery upside.</p>

            <h3 className="font-semibold mt-4">Data Source</h3>
            <p className="text-muted-foreground text-xs">Automated via Yahoo Finance. Scanned daily at 5:00 PM AEST after market close.</p>
          </div>
        </div>

        {/* ============ 5. TIWISTOCK-2 (SmartBuyScoreV2) ============ */}
        <div className="card border-emerald-500/30 bg-gradient-to-br from-emerald-900/10 to-background" data-testid="about-tiwistock-v2">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-emerald-400" />
            <h2 className="text-lg font-semibold flex items-center gap-2">
              Tiwistock-2
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-bold tracking-wider">V2 ✨</span>
            </h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Tiwistock-2 is not just a ranking tool. It is a <strong className="text-foreground">shortlist and timing-support engine</strong> that combines analyst conviction, fundamentals, trend, liquidity, sector risk, and entry quality. It helps assess <strong className="text-foreground">whether</strong> a stock may be worth considering, <strong className="text-foreground">whether the entry timing</strong> looks favourable, and <strong className="text-foreground">why</strong> it should or should not be shortlisted. Runs alongside the original Smart Rankings — both models remain available for comparison.
          </p>

          {/* Disclaimer at top of V2 section */}
          <div className="mb-4 p-3 rounded-md bg-yellow-900/10 border border-yellow-500/30 text-xs text-muted-foreground" data-testid="v2-disclaimer">
            <strong className="text-yellow-400">Important:</strong> Tiwistock-2 is an informational decision-support tool only. It does not provide personal financial advice. Always do your own research and consider your risk profile before buying or selling shares.
          </div>

          {/* What labels mean */}
          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">Final Labels</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/30"><strong className="text-emerald-300">Top Pick</strong> <span className="text-muted-foreground">(85-100)</span> — best combination of analyst support, fundamentals, trend, liquidity, and risk control</div>
              <div className="p-2 rounded bg-green-500/10 border border-green-500/30"><strong className="text-green-300">Smart Buy</strong> <span className="text-muted-foreground">(75-84)</span> — attractive opportunity, may need timing confirmation. <em className="text-yellow-300">Smart Buy does not mean buy immediately — always check Entry Quality before acting.</em></div>
              <div className="p-2 rounded bg-lime-500/10 border border-lime-500/30"><strong className="text-lime-300">Buy / Accumulate</strong> <span className="text-muted-foreground">(65-74)</span> — good stock, not quite Top Pick</div>
              <div className="p-2 rounded bg-yellow-500/10 border border-yellow-500/30"><strong className="text-yellow-300">Watchlist</strong> <span className="text-muted-foreground">(50-64)</span> — monitor, wait for better trend or risk profile</div>
              <div className="p-2 rounded bg-orange-500/10 border border-orange-500/30"><strong className="text-orange-300">Speculative / High Risk</strong> <span className="text-muted-foreground">(35-49)</span> — possible upside but high uncertainty</div>
              <div className="p-2 rounded bg-red-500/10 border border-red-500/30"><strong className="text-red-300">Avoid</strong> <span className="text-muted-foreground">(&lt;35)</span> — weak fundamentals, dangerous debt, poor cash flow, or major red flags</div>
            </div>

            {/* Scoring formula */}
            <h3 className="font-semibold mt-4">How Smart Buy Score V2 is Calculated</h3>
            <div className="p-3 rounded bg-muted/30 font-mono text-xs text-muted-foreground leading-relaxed">
              Smart Buy Score V2 =<br/>
              &nbsp;&nbsp;Analyst Score × <strong className="text-emerald-400">30%</strong><br/>
              + Value / Recovery × <strong className="text-emerald-400">20%</strong><br/>
              + Financial Strength × <strong className="text-emerald-400">20%</strong><br/>
              + Activity / Liquidity × <strong className="text-emerald-400">10%</strong><br/>
              + Trend / Momentum × <strong className="text-emerald-400">10%</strong><br/>
              + Risk / Quality × <strong className="text-emerald-400">10%</strong>
            </div>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">Analyst (30%)</strong> — normalised buy_count × growth_potential from MarketIndex broker consensus</p>
              <p><strong className="text-foreground">Value / Recovery (20%)</strong> — upside to 52-week high, capped to avoid value-trap traps</p>
              <p><strong className="text-foreground">Financial Strength (20%)</strong> — the 5-pillar / 10-metric percentage from the Financial Strength engine</p>
              <p><strong className="text-foreground">Activity (10%)</strong> — normalised value traded (preferred over raw volume)</p>
              <p><strong className="text-foreground">Trend (10%)</strong> — 50DMA / 200DMA position plus 3M / 6M / 1Y returns</p>
              <p><strong className="text-foreground">Risk / Quality (10%)</strong> — starts at 100, deducts for red flags (debt, dilution, negative cash, weak coverage, etc.)</p>
            </div>

            {/* Hard caps */}
            <h3 className="font-semibold mt-4">Hard Caps (Override the Score)</h3>
            <p className="text-muted-foreground text-xs mb-2">Even a high calculated score can be reduced if these protective rules trigger:</p>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>• <strong className="text-red-300">Debt / Equity &gt; 1.5</strong> → Avoid</p>
              <p>• <strong className="text-foreground">Financial Strength &lt; 40%</strong> → max Speculative / High Risk</p>
              <p>• <strong className="text-foreground">Financial Strength 40–49%</strong> → max Watchlist</p>
              <p>• <strong className="text-foreground">Financial Strength 50–59%</strong> → max Buy / Accumulate</p>
              <p>• <strong className="text-foreground">Financial Strength 60–69%</strong> → max Smart Buy (cannot be Top Pick)</p>
              <p>• <strong className="text-foreground">Below both 50DMA & 200DMA + negative 3M and 6M</strong> → max Watchlist</p>
              <p>• <strong className="text-foreground">Negative free cash flow AND negative operating cash flow</strong> → max Watchlist</p>
              <p>• <strong className="text-foreground">Buy count &lt; 3 analysts</strong> → max Buy / Accumulate</p>
              <p>• <strong className="text-foreground">Financial data unavailable</strong> → max Watchlist (Data Incomplete flag)</p>
            </div>

            {/* Entry Quality */}
            <h3 className="font-semibold mt-4">Entry Quality</h3>
            <p className="text-muted-foreground text-xs mb-2">A separate signal telling you <strong className="text-foreground">when</strong> to enter:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded bg-muted/30"><strong className="text-emerald-300">Excellent Entry</strong> — Smart Buy / Top Pick with strong trend (above 50DMA, trend ≥ 70)</div>
              <div className="p-2 rounded bg-muted/30"><strong className="text-green-300">Good Entry</strong> — Smart Buy or Buy/Acc with FS ≥ 60 and trend ≥ 55</div>
              <div className="p-2 rounded bg-muted/30"><strong className="text-yellow-300">Wait for Pullback</strong> — strong stock that has run too far in short term (1M &gt; 15% or day &gt; 5%)</div>
              <div className="p-2 rounded bg-muted/30"><strong className="text-amber-300">Wait for Trend Confirmation</strong> — fundamentally fine but currently below 50DMA or 200DMA</div>
              <div className="p-2 rounded bg-muted/30"><strong className="text-red-300">Avoid Entry</strong> — Avoid label OR Debt/Equity &gt; 1.5 OR negative FCF+OCF OR FS &lt; 40%</div>
            </div>

            <div className="mt-3 p-3 rounded-md bg-blue-500/10 border border-blue-500/20 text-xs text-muted-foreground" data-testid="entry-quality-example">
              <strong className="text-blue-300">Example:</strong> A stock can be <strong className="text-foreground">Buy / Accumulate</strong> but still show <strong className="text-amber-300">Wait for Trend Confirmation</strong> if the fundamentals are good but the price is currently below key moving averages. The label tells you the stock is worth shortlisting; Entry Quality tells you the timing is not right yet.
            </div>

            {/* Phase 2 — Sector + Mining */}
            <h3 className="font-semibold mt-4 flex items-center gap-2">
              <Layers className="w-4 h-4 text-blue-400" />
              Sector Adjustment Layer (Phase 2)
            </h3>
            <p className="text-muted-foreground text-xs">
              After the base score is computed, a second pass applies <strong className="text-foreground">protective penalties</strong> for sector-specific risks (Phase 2 does not add positive bonuses to non-mining sectors):
            </p>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>• <strong className="text-foreground">Technology stocks with P/E &gt; 50</strong> → −5 (premium valuation warning)</p>
              <p>• <strong className="text-foreground">Utilities with debt / equity &gt; 1.0</strong> → −10 (rate-sensitivity risk)</p>
              <p>• <strong className="text-foreground">REITs with negative free cash flow</strong> → −5 (distribution sustainability warning)</p>
              <p>Each affected stock shows <strong className="text-foreground">Base score → Sector adjusted score</strong> with the reason on the card.</p>
            </div>

            {/* Phase 2 — Mining */}
            <h3 className="font-semibold mt-4 flex items-center gap-2">
              <Pickaxe className="w-4 h-4 text-amber-400" />
              Mining / Resources Engine (Phase 2)
            </h3>
            <p className="text-muted-foreground text-xs mb-2">
              Resource stocks are evaluated separately because they are commodity-cyclical. The mining engine automatically classifies each resource stock by stage and commodity, then applies extra caps:
            </p>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>• <strong className="text-foreground">Mining Stage</strong> — Major Producer / Mid-Tier / Small Producer / Developer / Explorer (heuristic + optional admin override)</p>
              <p>• <strong className="text-foreground">Primary Commodity</strong> — Gold, Copper, Lithium, Uranium, Iron Ore, Coal, Rare Earths, Nickel, Oil & Gas, Silver, Diversified</p>
              <p>• <strong className="text-foreground">Commodity Trend Score</strong> (0-100) — auto-derived from futures / ETF proxies (GC=F, HG=F, URA, LIT, REMX, CL=F, etc.) with optional admin override</p>
              <p>• <strong className="text-foreground">Production Quality</strong> — coarse signal from profit margin + OCF/NI ratio</p>
              <p>• <strong className="text-foreground">Dilution Risk</strong> — flagged when share count growth &gt; 15% YoY</p>
            </div>
            <div className="mt-2 space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">Mining-specific caps:</strong></p>
              <p>• <strong className="text-amber-300">Explorer</strong> → max Speculative / High Risk</p>
              <p>• <strong className="text-amber-300">Developer with negative operating cash flow</strong> → max Watchlist</p>
              <p>• <strong className="text-amber-300">Commodity Trend Score &lt; 35</strong> → max Watchlist (protects from weak gold / uranium cycles)</p>
              <p>• <strong className="text-amber-300">Negative free cash flow + high dilution</strong> → −15 to −25 penalty</p>
              <p>• <strong className="text-amber-300">Commodity Trend Score ≥ 70</strong> → +3 bonus (the only positive Phase 2 adjustment, mining-exclusive)</p>
            </div>
            <p className="text-muted-foreground text-xs mt-2">
              Switch between <strong className="text-foreground">All Stocks</strong> and <strong className="text-amber-300">Mining / Resources</strong> sub-tabs at the top of the page. The Mining tab includes a live commodity trend snapshot and extra filters for stage and commodity.
            </p>

            {/* AI Insight */}
            <h3 className="font-semibold mt-4">AI Insight</h3>
            <p className="text-muted-foreground text-xs">
              Each stock card shows a plain-English summary explaining the rating, positive signals, risk flags, cap reasons, and entry status — so you never have to guess why the model rated a stock the way it did.
            </p>

            {/* How to use */}
            <h3 className="font-semibold mt-4">How to Use Tiwistock-2 for Shortlisting</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3">Signal</th><th className="py-1.5">Action</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/40"><td className="py-1.5 pr-3"><strong className="text-emerald-300">Smart Buy + Excellent Entry</strong></td><td>Best immediate candidate</td></tr>
                  <tr className="border-b border-border/40"><td className="py-1.5 pr-3"><strong className="text-green-300">Buy / Accumulate + Good Entry</strong></td><td>Good staged-entry candidate</td></tr>
                  <tr className="border-b border-border/40"><td className="py-1.5 pr-3"><strong className="text-yellow-300">Smart Buy + Wait for Pullback</strong></td><td>Good stock, but wait</td></tr>
                  <tr className="border-b border-border/40"><td className="py-1.5 pr-3"><strong className="text-amber-300">Buy / Accumulate + Wait for Trend</strong></td><td>Watch, not immediate buy</td></tr>
                  <tr className="border-b border-border/40"><td className="py-1.5 pr-3"><strong className="text-foreground">Watchlist</strong></td><td>Monitor only</td></tr>
                  <tr><td className="py-1.5 pr-3"><strong className="text-red-300">Avoid</strong></td><td>Ignore unless doing special research</td></tr>
                </tbody>
              </table>
            </div>

            {/* Two-layer architecture */}
            <h3 className="font-semibold mt-4">How the App Stays Fast</h3>
            <p className="text-muted-foreground text-xs">
              Data fetching and scoring are split into two layers. <strong className="text-blue-300">Refresh Market Data</strong> (slow, ~5-10 min) fetches yfinance trend data, financial strength, and commodity trends — only stale symbols (cached &lt; 20h) are re-fetched. <strong className="text-emerald-300">Recalculate V2</strong> (fast, ~45 sec) scores from cached MongoDB data only, with no live yfinance calls. Phase 2 sector/mining layer runs automatically at the end.
            </p>

            <h3 className="font-semibold mt-4">Best Use</h3>
            <p className="text-muted-foreground text-xs" data-testid="best-use-text">
              Use Tiwistock-2 as a <strong className="text-foreground">shortlist engine</strong>. Start with <strong className="text-green-300">Smart Buy</strong> and <strong className="text-lime-300">Buy / Accumulate</strong> stocks, then use <strong className="text-foreground">Entry Quality</strong>, <strong className="text-foreground">cap reasons</strong>, and <strong className="text-foreground">AI Insight</strong> to decide whether to act, wait, or ignore.
            </p>

            <h3 className="font-semibold mt-4">Data Sources</h3>
            <p className="text-muted-foreground text-xs">
              Yahoo Finance (price, MA, returns, financials, commodity proxies), MarketIndex broker consensus (analyst counts, target prices), and existing TiwiStock collections (Top Picks, Active Stocks, 52-week Lows, Financial Strength).
            </p>
          </div>
        </div>

        {/* ============ 6. FINANCIAL STRENGTH ============ */}
        <div className="card" data-testid="about-financial-strength">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-emerald-500" />
            <h2 className="text-lg font-semibold">Financial Strength</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Professional fundamental analysis of 250+ ASX stocks across <strong className="text-foreground">5 pillars and 10 metrics</strong>. Each metric is scored 0-2 using a practical, range-based model designed for real-world investing. Extreme values (like unsustainable growth) are penalised, not rewarded.
          </p>

          {/* 5 Pillars */}
          <div className="space-y-4 text-sm">
            <h3 className="font-semibold">The 5 Pillars</h3>

            {/* Valuation */}
            <div className="p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-emerald-400 mb-2">A. Valuation</h4>
              <p className="text-xs text-muted-foreground mb-2">Are you paying a fair price for the company?</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">P/E Ratio (Price-to-Earnings)</span>
                  <span>&lt;15 = 2pts | 15-30 = 1pt | &gt;30 = 0</span>
                </div>
                <p className="text-[10px] ml-2">A P/E below 15 suggests the stock may be undervalued. Above 30 means investors are paying a premium — often for growth stocks.</p>
                <div className="flex justify-between mt-2">
                  <span className="font-medium text-foreground">Price-to-Book</span>
                  <span>&lt;3 = 2pts | 3-6 = 1pt | &gt;6 = 0</span>
                </div>
                <p className="text-[10px] ml-2">Compares the stock price to the company's book value (assets minus liabilities). Lower means you're buying assets cheaper.</p>
              </div>
            </div>

            {/* Financial Health */}
            <div className="p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-blue-400 mb-2">B. Financial Health</h4>
              <p className="text-xs text-muted-foreground mb-2">Can the company pay its bills and manage its debt?</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">Current Ratio</span>
                  <span>&gt;1.5 = 2pts | 1-1.5 = 1pt | &lt;1 = 0</span>
                </div>
                <p className="text-[10px] ml-2">Current assets divided by current liabilities. Above 1.5 means the company can comfortably cover its short-term debts.</p>
                <div className="flex justify-between mt-2">
                  <span className="font-medium text-foreground">Quick Ratio</span>
                  <span>&ge;1 = 2pts | 0.5-1 = 1pt | &lt;0.5 = 0</span>
                </div>
                <p className="text-[10px] ml-2">Like current ratio but excludes inventory — a stricter test of immediate liquidity.</p>
                <div className="flex justify-between mt-2">
                  <span className="font-medium text-foreground">Debt/Equity</span>
                  <span>&lt;0.5 = 2pts | 0.5-1 = 1pt | &gt;1 = 0</span>
                </div>
                <p className="text-[10px] ml-2">Total debt divided by shareholder equity. Below 0.5 is very safe. <strong className="text-red-400">Above 1.5 triggers an automatic AVOID rating</strong> regardless of other scores.</p>
              </div>
            </div>

            {/* Cash Flow */}
            <div className="p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-cyan-400 mb-2">C. Cash Flow Quality</h4>
              <p className="text-xs text-muted-foreground mb-2">Is the company generating real cash, not just paper profits?</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">Free Cash Flow</span>
                  <span>Positive = 2pts | Negative = 0</span>
                </div>
                <p className="text-[10px] ml-2">Cash left after paying for operations and capital expenditure. Positive means the company generates real money.</p>
                <div className="flex justify-between mt-2">
                  <span className="font-medium text-foreground">OCF vs Net Income</span>
                  <span>OCF &gt; NI = 2pts | OCF &lt; NI = 0</span>
                </div>
                <p className="text-[10px] ml-2">If operating cash flow exceeds reported profit, earnings are backed by real cash — a sign of quality. If not, profits may include accounting adjustments that don't represent actual cash.</p>
              </div>
            </div>

            {/* Growth */}
            <div className="p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-yellow-400 mb-2">D. Growth</h4>
              <p className="text-xs text-muted-foreground mb-2">Is the company growing at a sustainable pace?</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">Revenue Growth</span>
                  <span>10-30% = 2pts | 30-80% = 1pt | &lt;10% or &gt;80% = 0</span>
                </div>
                <p className="text-[10px] ml-2">Annual revenue growth rate. <strong className="text-yellow-400">10-30% is the sweet spot.</strong> Growth above 80% is penalised because it is often one-time, acquisition-driven, or from a tiny revenue base — and rarely sustained.</p>
              </div>
            </div>

            {/* Profitability */}
            <div className="p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-purple-400 mb-2">E. Profitability</h4>
              <p className="text-xs text-muted-foreground mb-2">Is the company making money efficiently?</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">Return on Equity (ROE)</span>
                  <span>&gt;20% = 2pts | 10-20% = 1pt | &lt;10% = 0</span>
                </div>
                <p className="text-[10px] ml-2">How much profit the company generates per dollar of shareholder equity. Above 20% is excellent.</p>
                <div className="flex justify-between mt-2">
                  <span className="font-medium text-foreground">Net Profit Margin</span>
                  <span>&gt;15% = 2pts | 5-15% = 1pt | &lt;5% = 0</span>
                </div>
                <p className="text-[10px] ml-2">Percentage of revenue that becomes profit after all expenses. Above 15% is strong.</p>
              </div>
            </div>
          </div>

          {/* Safety Layer */}
          <div className="mt-4 space-y-3 text-sm">
            <h3 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Safety Layer (Hard Filters)
            </h3>
            <p className="text-muted-foreground text-xs">
              Before calculating the final score, a safety check is applied:
            </p>
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-muted-foreground">
              <p className="text-red-400 font-semibold mb-2">Automatic AVOID if:</p>
              <p>Debt/Equity ratio exceeds 1.5 — regardless of how well other metrics score. This protects against companies with dangerous levels of debt.</p>
            </div>
          </div>

          {/* Red Flags */}
          <div className="mt-4 space-y-3 text-sm">
            <h3 className="font-semibold">Red Flags</h3>
            <p className="text-muted-foreground text-xs">Additional warnings are shown when expanding a stock's details:</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>* Revenue growth above 80% — may not be sustainable</p>
              <p>* Negative free cash flow — company is burning cash</p>
              <p>* Operating cash flow below net income — earnings quality concern</p>
              <p>* Low P/E with high growth — verify the valuation is genuine</p>
              <p>* Unusually high profit margin (&gt;40%) — check if it's a temporary spike</p>
            </div>
          </div>

          {/* Rating System */}
          <div className="mt-4 space-y-3 text-sm">
            <h3 className="font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-500" />
              Rating System
            </h3>
            <p className="text-muted-foreground text-xs">
              Each metric scores 0 (poor), 1 (moderate), or 2 (strong). The total is converted to a percentage of the maximum possible score (only metrics with available data are counted), and a rating is assigned:
            </p>
            <div className="space-y-1.5">
              {[
                { rating: 'Strong Buy', range: '80% and above', color: 'bg-emerald-500', desc: 'Excellent fundamentals across most pillars' },
                { rating: 'Buy', range: '60% - 79%', color: 'bg-green-500', desc: 'Good fundamentals — a balanced, solid company' },
                { rating: 'Hold', range: '40% - 59%', color: 'bg-yellow-500', desc: 'Average — some strengths but notable weaknesses' },
                { rating: 'Avoid', range: 'Below 40% or Debt/Equity > 1.5', color: 'bg-red-500', desc: 'Weak fundamentals or dangerous debt levels' },
              ].map(({ rating, range, color, desc }) => (
                <div key={rating} className="flex items-start gap-3 p-2 rounded bg-muted/30">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold text-white ${color} whitespace-nowrap mt-0.5`}>{rating}</span>
                  <div>
                    <span className="text-xs font-medium text-foreground">{range}</span>
                    <p className="text-[10px] text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="font-semibold mt-4">AI Insight</h3>
            <p className="text-muted-foreground text-xs">Each stock gets a plain-English summary explaining its valuation, profitability, debt level, and any risk flags. Shown when you click to expand a stock row.</p>

            <h3 className="font-semibold mt-4">Data Source</h3>
            <p className="text-muted-foreground text-xs">All financial data is fetched from <strong className="text-foreground">Yahoo Finance</strong>. Analysis runs daily at 6:00 PM AEST. Admins can also trigger a manual refresh from the Upload page.</p>
          </div>
        </div>

        {/* ============ 6. PORTFOLIO ============ */}
        <div className="card" data-testid="about-portfolio">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="w-6 h-6 text-purple-500" />
            <h2 className="text-lg font-semibold">Portfolio</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Your personal watchlist and tracker. Add any stock from the Top Picks, Favourite Stocks, or High Potential pages using the + button.
          </p>

          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">Features</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p><strong className="text-foreground">Add/Remove</strong> — Click the + icon on any stock card to add it to your portfolio. Click again to remove.</p>
              <p><strong className="text-foreground">Quantity Tracking</strong> — Enter how many shares you hold (by number or dollar amount).</p>
              <p><strong className="text-foreground">Current Value</strong> — Calculated using the latest prices from Yahoo Finance.</p>
              <p><strong className="text-foreground">Cloud Synced</strong> — Your portfolio is saved to your account in the database, so it persists across devices.</p>
            </div>
          </div>
        </div>

        {/* ============ 7. DAILY NEWS ============ */}
        <div className="card" data-testid="about-news">
          <div className="flex items-center gap-3 mb-4">
            <Newspaper className="w-6 h-6 text-blue-500" />
            <h2 className="text-lg font-semibold">Daily News</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Live financial news from Australia's leading business publications, pulled via RSS feeds.
          </p>

          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">News Sources</h3>
            <div className="space-y-1.5 text-muted-foreground text-xs">
              <p>* <strong className="text-foreground">Sydney Morning Herald (SMH)</strong> — Business section</p>
              <p>* <strong className="text-foreground">Australian Financial Review (AFR)</strong> — Markets section</p>
              <p>* <strong className="text-foreground">ABC News</strong> — Business section</p>
            </div>
            <p className="text-muted-foreground text-xs mt-2">News is fetched fresh each time you open the page. You can filter by source using the category tabs.</p>
          </div>
        </div>

        {/* ============ UPDATE SCHEDULE ============ */}
        <div className="card" data-testid="about-schedule">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-yellow-500" />
            <h2 className="text-lg font-semibold">Daily Update Schedule</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            All automated data refreshes happen after the ASX market closes at 4:00 PM AEST:
          </p>

          <div className="space-y-3 text-sm">
            {[
              { time: '4:00 PM AEST', task: 'Health Ping', desc: 'Google Cloud Scheduler wakes the backend server to ensure it is ready for the data jobs.', color: 'bg-gray-500' },
              { time: '5:00 PM AEST', task: 'Stock Data Scan', desc: 'Scans 250+ ASX stocks via Yahoo Finance. Updates the High Potential list (52-week analysis).', color: 'bg-green-500' },
              { time: '5:15 PM AEST', task: 'Price Refresh', desc: 'Updates current prices and daily change % for ALL stocks across Top Picks, Active Stocks, and High Potential.', color: 'bg-blue-500' },
              { time: '6:00 PM AEST', task: 'Financial Strength Analysis', desc: 'Runs the full 10-metric fundamental analysis on 250+ stocks. Generates scores, ratings, red flags, and insights.', color: 'bg-emerald-500' },
            ].map(({ time, task, desc, color }) => (
              <div key={task} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full ${color} mt-1.5 shrink-0`}></div>
                <div>
                  <div className="font-semibold text-xs">{time} — {task}</div>
                  <div className="text-muted-foreground text-xs">{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-muted-foreground text-xs mt-3">
            Top Picks and Favourite Stocks lists are uploaded manually by the admin and can be updated at any time via the Upload page.
          </p>
        </div>

        {/* ============ DISCLAIMER ============ */}
        <div className="card bg-yellow-900/10 border-yellow-500/30" data-testid="about-disclaimer">
          <h2 className="text-sm font-semibold mb-2 text-yellow-500">Disclaimer</h2>
          <p className="text-xs text-muted-foreground">
            This tool is for informational purposes only. The recommendations, scores, and ratings are generated by TiwiStock's own algorithms based on publicly available data from Yahoo Finance and broker research via MarketIndex. They do not constitute financial advice.
            Always conduct your own research and consider consulting with a licensed financial advisor before making investment decisions.
            Past performance does not guarantee future results.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AboutPage;
